import { useCallback } from 'react';
import { formatDateForFilename, sanitizeFilename } from '@orca360/shared';
import { useDocumentAPI } from './useDocumentAPI';
import type { DebriefingPartAData } from '../types/debriefing-export';
import type {
  PartB_DebriefingFramework,
  PartC_SupportingMaterial,
  PartD_InstructorNotes,
} from '../types/debriefing-guide';

export interface DebriefingGuideWordExportData {
  partA: DebriefingPartAData;
  partB?: PartB_DebriefingFramework;
  partC?: PartC_SupportingMaterial;
  partD?: PartD_InstructorNotes;
  invocationLine?: string;
}

export interface UseWordExportDebriefingOptions {
  onExportStart?: () => void;
  onExportSuccess?: (blob: Blob, filename: string) => void;
  onExportError?: (error: Error) => void;
  includePartA?: boolean;
  includePartB?: boolean;
  includePartC?: boolean;
  includePartD?: boolean;
  includePartE?: boolean;
}

export interface UseWordExportDebriefingReturn {
  exportDebriefingToWord: (data: DebriefingGuideWordExportData, sessionId?: string) => Promise<Blob>;
  isExporting: boolean;
  error: Error | null;
}

export function useWordExportDebriefing(
  options: UseWordExportDebriefingOptions = {},
): UseWordExportDebriefingReturn {
  const { generateDocument, isExporting, error } = useDocumentAPI(options);

  const exportDebriefingToWord = useCallback(
    async (data: DebriefingGuideWordExportData, sessionId?: string): Promise<Blob> => {
      const sessionDate = formatDateForFilename(data.partA?.sessionDate || 'export');
      const traineeName = sanitizeFilename(data.partA?.trainee?.name || 'trainee');
      const defaultFilename = `WingMentor_Debriefing_${sessionDate}_${traineeName}.docx`;
      // sessionId lets the endpoint resolve the owning tenant's effective
      // framework version for authority exports (HG-2B G2-05d).
      return generateDocument('debriefing-guide', 'docx', data as unknown as Record<string, unknown>, defaultFilename, sessionId);
    },
    [generateDocument],
  );

  return { exportDebriefingToWord, isExporting, error };
}
